/**
 * El puntaje de la hoja de vida, del lado del candidato.
 *
 * No es una nota decorativa: es el mecanismo que empuja a mejorar el material.
 * Si nadie le dice a la persona que su hoja de vida va floja, no tiene por qué
 * arreglarla — y con material flojo no coloca, por muchas postulaciones que
 * mandemos. Por eso el puntaje vive en las TRES pantallas donde se decide algo:
 * al revisar el CV recién escrito, en el perfil cada vez que vuelve, y como
 * aviso en el panel cuando va bajo.
 *
 * ── DOS REGLAS DE ESTE ARCHIVO ──────────────────────────────────────────────
 *
 * 1. `null` NO ES CERO. Un CV anterior al puntaje llega con `cv_score: null`, y
 *    eso significa "todavía no se calculó", no "sacó cero". Pintar un cero ahí
 *    sería acusar a alguien de algo que nunca medimos. Quien recibe null pide
 *    `GET /api/cv/score` una vez (ahí se calcula y se guarda) — nunca dibuja 0.
 *
 * 2. EL COPY MOTIVA, NO REGAÑA. "Estás a dos datos de una hoja de vida fuerte"
 *    y no "puntaje bajo". La persona que llega a esta pantalla ya sabe que
 *    necesita trabajo; lo que no sabe es QUÉ le falta, y eso es lo único que
 *    tiene sentido decirle.
 */
import type { ToneName } from '~/components/ui';
import { messages } from '~/i18n';

export interface CVScoreSubscores {
  experience: number;
  skills: number;
  specificity: number;
  completeness: number;
}

export interface CVScoreMissing {
  /** Qué falta, en palabras de la persona: "años con montacargas". */
  field: string;
  /** Por qué le importa a quien contrata. */
  why_it_matters: string;
  /** Qué hacer hoy para taparlo. Accionable o no sirve. */
  how_to_fix: string;
}

export interface CVScoreDetail {
  score: number;
  subscores: CVScoreSubscores;
  missing: CVScoreMissing[];
  strengths: string[];
  scored_at?: string | null;
}

/** Debajo de acá el panel avisa. No es una nota de corte: es cuándo vale interrumpir. */
export const WEAK_SCORE = 60;

/**
 * Las tres bandas. Los cortes son los del contrato del score y el color sale del
 * semáforo de la marca, sin inventar un pigmento:
 *
 *   flojo  (<50)    coral   → fricción: esto se cae si sale así
 *   camino (50-69)  púrpura → lo que está en marcha, que es exactamente el caso
 *   fuerte (≥70)    mint    → lista para enviar
 *
 * El corte fuerte es 70 y no 76 (2026-10-30, Pacho y Clovis): con 70 la hoja ya
 * sale bien, y la gente necesitaba ver en verde que puede parar de agregar.
 *
 * El ámbar del brief no existe en los tokens de TBit, y el púrpura ya significa
 * "en marcha" en toda la app: inventar un cuarto color para decir lo mismo
 * rompería el semáforo en la única pantalla donde el color tiene que enseñar.
 */
export type ScoreBand = 'weak' | 'fair' | 'strong';

export function scoreBand(score: number): ScoreBand {
  if (score < 50) return 'weak';
  if (score < 70) return 'fair';
  return 'strong';
}

export function scoreTone(score: number): ToneName {
  const band = scoreBand(score);
  return band === 'strong' ? 'good' : band === 'fair' ? 'info' : 'bad';
}

/** El color del arco y del número, como variable de marca. */
export function scoreColorVar(score: number): string {
  const band = scoreBand(score);
  if (band === 'strong') return 'var(--mint-ink)';
  if (band === 'fair') return 'var(--purple-cta)';
  return 'var(--coral-ink)';
}

/**
 * La etiqueta corta que acompaña al número, en el idioma activo. Lee el
 * diccionario al llamarse (nunca a nivel de módulo, que congelaría el idioma).
 */
export function scoreLabel(score: number): string {
  return messages().onboarding.cvScore.labels[scoreBand(score)];
}

/**
 * La frase que empuja. Nombra CUÁNTOS datos faltan, porque un número pequeño y
 * concreto ("dos datos") es lo que hace que alguien vuelva a abrir el
 * cuestionario; "tu puntaje es bajo" no le pide nada a nadie.
 */
export function scoreHeadline(detail: CVScoreDetail): string {
  const gaps = detail.missing.length;
  const band = scoreBand(detail.score);
  const h = messages().onboarding.cvScore.headline;

  if (band === 'strong') return gaps === 0 ? h.strongReady : h.strongGaps(gaps);
  if (band === 'fair') return gaps === 0 ? h.fairNoGaps : h.fairGaps(gaps);
  return gaps === 0 ? h.weakNoGaps : h.weakGaps(gaps);
}

/**
 * Los cuatro sub-puntajes, en el orden en que se leen. El nombre y la pista de
 * cada uno viven en el diccionario (`onboarding.cvScore.subscores[key]`).
 */
export const SUBSCORES: Array<keyof CVScoreSubscores> = [
  'experience',
  'skills',
  'specificity',
  'completeness',
];

function clampScore(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Lo que llega del servidor, vuelto algo que la pantalla puede pintar sin
 * preguntarse nada.
 *
 * Devuelve `null` cuando NO hay puntaje — que es distinto de un puntaje en cero
 * y por eso no se puede colapsar en un número. Un CV viejo llega así hasta la
 * primera lectura de `/api/cv/score`, y la pantalla que reciba null tiene que
 * pedirlo, no dibujar una nota que nadie calculó.
 */
export function normalizeScoreDetail(raw: unknown): CVScoreDetail | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, any>;
  if (d.score === null || d.score === undefined) return null;

  const subs = (d.subscores ?? {}) as Record<string, unknown>;
  const missing = Array.isArray(d.missing) ? d.missing : [];
  const strengths = Array.isArray(d.strengths) ? d.strengths : [];

  return {
    score: clampScore(d.score),
    subscores: {
      experience: clampScore(subs.experience),
      skills: clampScore(subs.skills),
      specificity: clampScore(subs.specificity),
      completeness: clampScore(subs.completeness),
    },
    missing: missing
      .filter((m: any) => m && typeof m.field === 'string' && typeof m.how_to_fix === 'string')
      .map((m: any) => ({
        field: String(m.field),
        why_it_matters: String(m.why_it_matters ?? ''),
        how_to_fix: String(m.how_to_fix),
      })),
    strengths: strengths.filter((s: unknown) => typeof s === 'string' && s.trim()).map(String),
    scored_at: typeof d.scored_at === 'string' ? d.scored_at : null,
  };
}
